import type { GetProjectLiveStatusBffResponse } from "..";
import type { Client, RequestContext } from "../client";
import type { FetchContainerLogsResponse } from "../resources/fetchContainerLogsResponse";
import type { FetchEventsResponse } from "../resources/fetchEventsResponse";
import type { GetLiveResourceResponse } from "../resources/getKubernetesLiveResourceResponse";
import type { GetLiveResourceManifestResponse } from "../resources/getLiveResourceManifestResponse";
import type { GetLiveStatusRollupBffResponse } from "../resources/getLiveStatusRollupBffResponse";
import type { KubernetesLiveStatusResponse } from "../resources/kubernetesLiveStatusResponse";
export class KubernetesLiveStatusRepository {
    private client: Client;
    constructor(client: Client, private readonly requestContext: RequestContext) {
        this.client = client;
    }
    getForApplicationInstance(projectId: string, environmentId: string, tenantId?: string) {
        if (tenantId === undefined)
            return this.client.get<KubernetesLiveStatusResponse>(this.requestContext, "~/api/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/untenanted/livestatus", {
                projectId: projectId,
                environmentId: environmentId,
            });
        return this.client.get<KubernetesLiveStatusResponse>(this.requestContext, "~/api/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/tenants/{tenantId}/livestatus", {
            projectId: projectId,
            environmentId: environmentId,
            tenantId: tenantId,
        });
    }
    getRollupForApplicationInstance(projectId: string, environmentId: string, tenantId?: string): Promise<GetLiveStatusRollupBffResponse> {
        if (tenantId === undefined) {
            return this.client.get<GetLiveStatusRollupBffResponse>(this.requestContext, "~/bff/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/untenanted/livestatus/rollup", {
                projectId: projectId,
                environmentId: environmentId,
            });
        }
        return this.client.get<GetLiveStatusRollupBffResponse>(this.requestContext, "~/bff/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/tenants/{tenantId}/livestatus/rollup", {
            projectId: projectId,
            environmentId: environmentId,
            tenantId: tenantId,
        });
    }
    getLiveResourceDetails(projectId: string, environmentId: string, tenantId: string | undefined, machineId: string, resourceId: string) {
        if (tenantId === undefined) {
            return this.client.get<GetLiveResourceResponse>(this.requestContext, "~/api/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/untenanted/machines/{machineId}/resources/{desiredOrKubernetesMonitoredResourceId}", {
                projectId: projectId,
                environmentId: environmentId,
                machineId: machineId,
                desiredOrKubernetesMonitoredResourceId: resourceId,
            });
        }
        return this.client.get<GetLiveResourceResponse>(this.requestContext, "~/api/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/tenants/{tenantId}/machines/{machineId}/resources/{desiredOrKubernetesMonitoredResourceId}", {
            projectId: projectId,
            environmentId: environmentId,
            tenantId: tenantId,
            machineId: machineId,
            desiredOrKubernetesMonitoredResourceId: resourceId,
        });
    }
    getLiveResourceManifest(projectId: string, environmentId: string, tenantId: string | undefined, machineId: string, resourceId: string): Promise<GetLiveResourceManifestResponse> {
        if (tenantId === undefined) {
            return this.client.get<GetLiveResourceManifestResponse>(this.requestContext, "~/bff/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/untenanted/machines/{machineId}/resources/{desiredOrKubernetesMonitoredResourceId}/manifest", {
                projectId: projectId,
                environmentId: environmentId,
                machineId: machineId,
                desiredOrKubernetesMonitoredResourceId: resourceId,
            });
        }
        return this.client.get<GetLiveResourceManifestResponse>(this.requestContext, "~/bff/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/tenants/{tenantId}/machines/{machineId}/resources/{desiredOrKubernetesMonitoredResourceId}/manifest", {
            projectId: projectId,
            environmentId: environmentId,
            tenantId: tenantId,
            machineId: machineId,
            desiredOrKubernetesMonitoredResourceId: resourceId,
        });
    }
    getLiveStatusForProject(projectId: string): Promise<GetProjectLiveStatusBffResponse> {
        return this.client.get<GetProjectLiveStatusBffResponse>(this.requestContext, "~/bff/spaces/{spaceId}/projects/{projectId}/livestatus", {
            projectId: projectId,
        });
    }
    beginContainerLogSession(projectId: string, environmentId: string, tenantId: string | undefined, machineId: string, resourceId: string, podName: string, containerName: string, showPreviousContainer: boolean): Promise<{
        SessionId: string;
    }> {
        return this.client.post<{
            SessionId: string;
        }>(this.requestContext, "~/api/spaces/{spaceId}/observability/logs/sessions", {
            ProjectId: projectId,
            EnvironmentId: environmentId,
            TenantId: tenantId,
            MachineId: machineId,
            PodName: podName,
            ContainerName: containerName,
            ShowPreviousContainer: showPreviousContainer,
            DesiredOrKubernetesMonitoredResourceId: resourceId,
        });
    }
    getContainerLogs(sessionId: string): Promise<FetchContainerLogsResponse> {
        return this.client.get<FetchContainerLogsResponse>(this.requestContext, "~/api/spaces/{spaceId}/observability/logs/sessions/{sessionId}", {
            sessionId,
        });
    }
    beginResourceEventsSession(projectId: string, environmentId: string, tenantId: string | undefined, machineId: string, resourceId: string): Promise<{
        SessionId: string;
    }> {
        return this.client.post<{
            SessionId: string;
        }>(this.requestContext, "~/api/spaces/{spaceId}/observability/events/sessions", {
            ProjectId: projectId,
            EnvironmentId: environmentId,
            TenantId: tenantId,
            MachineId: machineId,
            DesiredOrKubernetesMonitoredResourceId: resourceId,
        });
    }
    getResourceEvents(sessionId: string): Promise<FetchEventsResponse> {
        return this.client.get<FetchEventsResponse>(this.requestContext, "~/api/spaces/{spaceId}/observability/events/sessions/{sessionId}", {
            sessionId,
        });
    }
}
